import {
  HTTP_STATUS_AUTHENTICATION_EXPIRED,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_UNAUTHORIZED,
  isAxiosError,
  isExpectedRequestFailure,
} from '@/plugins/http'

const EXPECTED_BREADCRUMB_STATUSES = [
  HTTP_STATUS_UNAUTHORIZED,
  HTTP_STATUS_FORBIDDEN,
  HTTP_STATUS_AUTHENTICATION_EXPIRED,
]

// Ids inside a path make every request look like a different failure. Collapse
// them so the fingerprint groups by endpoint instead of by record.
const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

export class SentryLogger {
  static handle(env, event, hint) {
    if (env.NODE_ENV !== 'production') {
      return null
    }

    const exception = hint?.originalException

    if (isExpectedRequestFailure(exception)) {
      return null
    }

    // Older events, and anything Sentry captures without the original error,
    // can only be judged by the request that preceded them.
    if (!exception && SentryLogger.followsExpectedRequest(event)) {
      return null
    }

    if (isAxiosError(exception)) {
      event.fingerprint = SentryLogger.fingerprintOf(exception)
    }

    return event
  }

  static followsExpectedRequest(event) {
    const breadcrumbs = event.breadcrumbs ?? []
    const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1]

    return (
      lastBreadcrumb?.category === 'xhr' &&
      EXPECTED_BREADCRUMB_STATUSES.includes(lastBreadcrumb.data?.status_code)
    )
  }

  // Without this every request failure groups under a single Sentry issue,
  // because they all share the same axios stack frame.
  static fingerprintOf(error) {
    const method = `${error.config?.method ?? 'unknown'}`.toUpperCase()
    const status = error.response?.status ?? error.code ?? 'no-response'

    return [
      'axios',
      method,
      SentryLogger.endpointOf(error.config?.url),
      `${status}`,
    ]
  }

  static endpointOf(url) {
    if (!url) {
      return 'unknown'
    }

    return url
      .split('?')[0]
      .split('/')
      .map(segment => (ID_SEGMENT.test(segment) ? ':id' : segment))
      .join('/')
  }
}
